import { getAuthenticatedSupabaseClient } from '@/integrations/supabase/client';
import { v4 as uuidv4 } from 'uuid';
import { toast } from 'sonner';
import { logger } from '@/lib/logger';
import { validateFile, createSanitizedFile, logFileUploadAttempt } from '@/utils/fileValidation';
import { environment } from '@/config/environment';

// Helper function to get file extension
const getFileExtension = (filename: string): string => {
  return filename.slice(((filename.lastIndexOf('.') - 1) >>> 0) + 2);
};

// Upload a floor plan file to Supabase Storage
export const uploadFloorPlan = async (
  file: File,
  projectId: string,
  userId: string,
  orgId?: string,
): Promise<{
  filePath: string;
  fileType: string;
  error: Error | null;
}> => {
  try {
    // Determine file type based on extension
    const fileExt = getFileExtension(file.name).toLowerCase();
    const isCAD = ['.dwg', '.dxf', '.dgn', '.dwf'].includes(`.${fileExt}`);
    const isPDF = fileExt === 'pdf';
    const fileType = isCAD ? 'cad' : isPDF ? 'pdf' : 'image';

    // Validate file with security checks
    const validation = await validateFile(file, {
      type: fileType as any,
      strictMode: true, // Enable magic byte checking and content scanning
    });

    // Log the upload attempt for security monitoring
    logFileUploadAttempt(file, validation, userId, orgId);

    if (!validation.valid) {
      logger.error('File validation failed:', validation.error);
      toast.error(validation.error || 'File validation failed');
      return {
        filePath: '',
        fileType: '',
        error: new Error(validation.error || 'File validation failed'),
      };
    }

    // Use sanitized filename
    const sanitizedFile = validation.sanitizedName
      ? createSanitizedFile(file, validation.sanitizedName)
      : file;

    const fileName = `${uuidv4()}.${fileExt}`;
    const filePath = `${userId}/${projectId}/${fileName}`;

    logger.log(
      `Uploading validated file: ${validation.sanitizedName}, size: ${sanitizedFile.size} bytes, type: ${sanitizedFile.type}`,
    );

    // Debug: Check if file is actually a File object
    logger.debug('File object details:', {
      isFile: file instanceof File,
      isBlob: file instanceof Blob,
      constructor: file.constructor.name,
      type: file.type,
      name: file.name,
      size: file.size,
    });

    // Ensure we have a valid content type
    const contentType = file.type || `image/${fileExt}` || 'image/png';
    logger.debug('Using contentType:', contentType);

    // Upload sanitized file to Supabase Storage
    const supabase = await getAuthenticatedSupabaseClient();
    const { error: uploadError } = await supabase.storage
      .from('floor_plans')
      .upload(filePath, sanitizedFile, {
        cacheControl: '3600',
        upsert: false,
        contentType: contentType,
      });

    if (uploadError) {
      logger.error('Storage upload error:', uploadError);
      throw uploadError;
    }

    logger.log(`File uploaded successfully to path: ${filePath}`);

    // Make sure the file is publicly accessible
    const { data: publicData } = supabase.storage.from('floor_plans').getPublicUrl(filePath);

    // The getPublicUrl method doesn't return an error property, so we don't need to check for it
    logger.log('File public URL:', publicData.publicUrl);

    return {
      filePath,
      fileType: fileExt,
      error: null,
    };
  } catch (error) {
    logger.error('Error uploading floor plan:', error);
    return {
      filePath: '',
      fileType: '',
      error: error as Error,
    };
  }
};

// Create a floor plan record in the database
export const createFloorPlanRecord = async (
  projectId: string,
  name: string,
  filePath: string,
  fileType: string,
  createdByClerkId?: string | null,
) => {
  try {
    const insertData: any = {
      project_id: projectId,
      name,
      file_path: filePath,
      file_type: fileType,
      processing_status: 'pending', // Start with pending status
      ai_processed: false,
    };

    // Add creator ID if provided (for Clerk users)
    if (createdByClerkId) {
      insertData.created_by_clerk_id = createdByClerkId;
    }

    const supabase = await getAuthenticatedSupabaseClient();
    const { data, error } = await supabase.from('floor_plans').insert(insertData).select().single();

    if (error) {
      throw error;
    }

    return { data, error: null };
  } catch (error) {
    logger.error('Error creating floor plan record:', error);
    return { data: null, error };
  }
};

// Get floor plans for a project
export const getFloorPlans = async (projectId: string) => {
  try {
    const supabase = await getAuthenticatedSupabaseClient();
    const { data, error } = await supabase
      .from('floor_plans')
      .select('*')
      .eq('project_id', projectId)
      .order('created_at', { ascending: false });

    if (error) {
      throw error;
    }

    // Ensure each floor plan has updated_at timestamp for cache busting
    const floorPlansWithTimestamp = data?.map(fp => ({
      ...fp,
      updated_at: fp.updated_at || new Date().toISOString(),
    }));

    return { data: floorPlansWithTimestamp, error: null };
  } catch (error) {
    logger.error('Error fetching floor plans:', error);
    return { data: null, error };
  }
};

// Get the public URL for a floor plan with cache busting
export const getFloorPlanUrl = (filePath: string, updatedAt?: string): string => {
  try {
    if (!filePath) {
      logger.error('No file path provided to getFloorPlanUrl');
      return '';
    }

    // Log the file path for debugging
    logger.log('getFloorPlanUrl - Input file path:', filePath);

    const supabaseUrl =
      environment.supabase.url ||
      (typeof import.meta !== 'undefined' ? import.meta.env?.VITE_SUPABASE_URL : undefined) ||
      (typeof process !== 'undefined'
        ? (process.env?.VITE_SUPABASE_URL ?? process.env?.SUPABASE_URL)
        : undefined);

    if (!supabaseUrl) {
      logger.error('Supabase URL not configured; cannot build floor plan URL');
      return '';
    }

    const sanitizedPath = filePath.replace(/^\/+/, '');
    const publicUrl = `${supabaseUrl}/storage/v1/object/public/floor_plans/${sanitizedPath}`;

    // Add cache-busting parameter based on updated timestamp or current time
    const cacheBuster = updatedAt ? new Date(updatedAt).getTime() : Date.now();
    const separator = publicUrl.includes('?') ? '&' : '?';
    const urlWithCacheBuster = `${publicUrl}${separator}v=${cacheBuster}`;

    // Return the URL with cache busting
    logger.log('getFloorPlanUrl - Generated URL:', urlWithCacheBuster);
    return urlWithCacheBuster;
  } catch (error) {
    logger.error('Error generating floor plan URL:', error);
    return '';
  }
};

/**
 * VF-15: generate a short-lived SIGNED URL for a floor-plan object instead of the
 * public URL. Works whether the bucket is public or private (so it can be rolled
 * out incrementally, then the bucket flipped to private). Access is gated by the
 * caller's storage RLS at signing time; the returned URL then renders in <img>
 * without an auth header. Returns null on failure so callers can fall back.
 */
export const getFloorPlanSignedUrl = async (
  filePath: string,
  ttlSeconds = 3600,
): Promise<string | null> => {
  if (!filePath) {
    logger.error('No file path provided to getFloorPlanSignedUrl');
    return null;
  }
  try {
    const supabase = await getAuthenticatedSupabaseClient();
    const sanitizedPath = filePath.replace(/^\/+/, '');
    const { data, error } = await supabase.storage
      .from('floor_plans')
      .createSignedUrl(sanitizedPath, ttlSeconds);
    if (error || !data?.signedUrl) {
      logger.error('Failed to create signed floor plan URL:', error);
      return null;
    }
    return data.signedUrl;
  } catch (error) {
    logger.error('Error creating signed floor plan URL:', error);
    return null;
  }
};

// Get floor plan metadata including image dimensions
export const getFloorPlanMetadata = async (floorPlanId: string) => {
  try {
    logger.log('Fetching metadata for floor plan:', floorPlanId);
    const supabase = await getAuthenticatedSupabaseClient();
    const { data, error } = await supabase
      .from('floor_plan_metadata')
      .select('*')
      .eq('floor_plan_id', floorPlanId)
      .maybeSingle();

    if (error) {
      logger.error('Error fetching floor plan metadata:', error);
      throw error;
    }

    logger.log('Fetched metadata:', data);
    return { data, error: null };
  } catch (error) {
    logger.error('Error in getFloorPlanMetadata:', error);
    return { data: null, error };
  }
};

// Trigger AI processing for a floor plan
export const processFloorPlanWithAI = async (floorPlanId: string) => {
  try {
    // Update the processing status to 'processing'
    const supabase = await getAuthenticatedSupabaseClient();
    const { error: updateError } = await supabase
      .from('floor_plans')
      .update({ processing_status: 'processing' })
      .eq('id', floorPlanId);

    if (updateError) {
      throw updateError;
    }

    // Get the floor plan details
    const { data: floorPlanData, error: floorPlanError } = await supabase
      .from('floor_plans')
      .select('*')
      .eq('id', floorPlanId)
      .single();

    if (floorPlanError || !floorPlanData) {
      throw floorPlanError || new Error('Floor plan not found');
    }

    // Get the public URL for the floor plan
    const imageUrl = getFloorPlanUrl(floorPlanData.file_path);

    logger.log('Calling edge function for floor plan:', floorPlanId, 'with URL:', imageUrl);

    // Call the Supabase Edge Function to process the floor plan with AI detection
    try {
      const { data, error } = await supabase.functions.invoke('process-floor-plan', {
        body: { floorPlanId, imageUrl, stride: 448, threshold: 0.4 },
      });

      if (error) {
        logger.error('Edge function error:', error);
        throw error;
      }

      logger.log('Edge function response:', data);

      // Even if the edge function succeeds, still generate fixtures
      // as a backup in case the edge function didn't create any
      const { count } = await supabase
        .from('lighting_fixtures')
        .select('*', { count: 'exact', head: true })
        .eq('floor_plan_id', floorPlanId);

      if (!count || count === 0) {
        logger.log('No fixtures found after AI processing');
        // Mark as completed but not AI processed since no fixtures were detected
        await supabase
          .from('floor_plans')
          .update({
            processing_status: 'completed',
            ai_processed: false,
            processing_error: 'No fixtures detected by AI',
          })
          .eq('id', floorPlanId);

        toast.warning('No lighting fixtures were detected in the image');
      } else {
        logger.log(`Found ${count} fixtures for floor plan ${floorPlanId}`);
        // Mark the floor plan as processed even if fixtures were created by the edge function
        await supabase
          .from('floor_plans')
          .update({
            processing_status: 'completed',
            ai_processed: true,
          })
          .eq('id', floorPlanId);
      }

      toast.success('Floor plan processed successfully with AI');

      return { success: true, error: null };
    } catch (invocationError) {
      logger.error('Error invoking process-floor-plan function:', invocationError);

      // Mark as failed and return error instead of generating fake data
      await supabase
        .from('floor_plans')
        .update({
          processing_status: 'failed',
          ai_processed: false,
          processing_error: 'AI processing service unavailable',
        })
        .eq('id', floorPlanId);

      toast.error('AI processing service is unavailable. Please try again later.');
      return { success: false, error: invocationError };
    }
  } catch (error) {
    logger.error('Error processing floor plan with AI:', error);

    // Update floor plan status to 'failed'
    const supabase = await getAuthenticatedSupabaseClient();
    await supabase
      .from('floor_plans')
      .update({ processing_status: 'failed' })
      .eq('id', floorPlanId);

    toast.error('Failed to process floor plan with AI');
    return { success: false, error };
  }
};

// REMOVED: generateAndStoreSampleFixtures function that created fake data
// This function was creating fake fixtures when AI processing failed,
// undermining data integrity. Now we properly handle AI failures
// by marking the floor plan as failed and notifying the user.

// Get lighting fixtures for a floor plan
export const getLightingFixtures = async (floorPlanId: string) => {
  try {
    logger.log('Fetching lighting fixtures for floor plan:', floorPlanId);
    const supabase = await getAuthenticatedSupabaseClient();
    const { data, error } = await supabase
      .from('lighting_fixtures')
      .select('*')
      .eq('floor_plan_id', floorPlanId);

    if (error) {
      logger.error('Error in getLightingFixtures:', error);
      throw error;
    }

    logger.log('Fetched fixtures:', data?.length || 0);
    return { data, error: null };
  } catch (error) {
    logger.error('Error fetching lighting fixtures:', error);
    return { data: null, error };
  }
};

// Check if a circuit is empty and delete it if it is
export const deleteEmptyCircuit = async (circuitId: string) => {
  try {
    // Check if the circuit has any fixtures
    const supabase = await getAuthenticatedSupabaseClient();
    const { count, error: countError } = await supabase
      .from('lighting_fixtures')
      .select('id', { count: 'exact', head: true })
      .eq('circuit_id', circuitId);

    if (countError) {
      throw countError;
    }

    // If no fixtures are assigned to this circuit, delete it
    if (count === 0) {
      logger.log(`Circuit ${circuitId} has no fixtures, deleting it`);

      // First unassign any controls that reference this circuit
      const { error: unassignControlsError } = await supabase
        .from('lighting_controls')
        .update({ circuit_id: null })
        .eq('circuit_id', circuitId);

      if (unassignControlsError) {
        logger.error('Error unassigning controls from empty circuit:', unassignControlsError);
        // Continue with deletion even if control unassignment fails
      }

      const { error: deleteError } = await supabase.from('circuits').delete().eq('id', circuitId);

      if (deleteError) {
        throw deleteError;
      }

      return { deleted: true, error: null };
    }

    return { deleted: false, error: null };
  } catch (error) {
    logger.error('Error checking and deleting empty circuit:', error);
    return { deleted: false, error };
  }
};

// Update the assignFixtureToCircuit function to check for empty circuits
export const assignFixtureToCircuit = async (
  fixtureId: string,
  circuitId: string | null,
  callbacks?: { onCircuitDeleted?: () => Promise<void> },
) => {
  try {
    const supabase = await getAuthenticatedSupabaseClient();
    // Get the old circuit ID before reassigning
    const { data: fixture, error: getError } = await supabase
      .from('lighting_fixtures')
      .select('circuit_id')
      .eq('id', fixtureId)
      .single();

    if (getError) {
      const code = (getError as any)?.code;
      if (code === 'PGRST116' || code === '406') {
        logger.warn(
          'Fixture not found while assigning circuit; assuming already removed:',
          fixtureId,
        );
        return { error: null };
      }
      throw getError;
    }

    const oldCircuitId = fixture.circuit_id;

    // Assign fixture to new circuit
    const { error } = await supabase
      .from('lighting_fixtures')
      .update({ circuit_id: circuitId })
      .eq('id', fixtureId);

    if (error) {
      throw error;
    }

    // If the fixture was previously assigned to a different circuit, check if that circuit is now empty
    if (oldCircuitId && oldCircuitId !== circuitId) {
      const { deleted, error: deleteError } = await deleteEmptyCircuit(oldCircuitId);

      // If circuit was deleted and we have a callback, call it
      if (deleted && callbacks?.onCircuitDeleted) {
        logger.log(`Circuit ${oldCircuitId} was deleted, executing callback to update controls`);
        await callbacks.onCircuitDeleted();
      }

      if (deleteError) {
        logger.error('Error checking/deleting empty circuit:', deleteError);
      }
    }

    return { error: null };
  } catch (error) {
    logger.error('Error in assignFixtureToCircuit:', error);
    return { error };
  }
};

// Update the deleteFixture function to check for empty circuits
export const deleteFixture = async (
  fixtureId: string,
  callbacks?: { onCircuitDeleted?: () => Promise<void> },
) => {
  try {
    const supabase = await getAuthenticatedSupabaseClient();
    // Get the circuit ID before deleting
    const { data: fixture, error: getError } = await supabase
      .from('lighting_fixtures')
      .select('circuit_id')
      .eq('id', fixtureId)
      .single();

    if (getError) {
      const code = (getError as any)?.code;
      if (code === 'PGRST116' || code === '406') {
        logger.warn('Fixture not found during delete; skipping circuit cleanup:', fixtureId);
        return { error: null };
      }
      throw getError;
    }

    const circuitId = fixture.circuit_id;

    // Delete the fixture
    const { error } = await supabase.from('lighting_fixtures').delete().eq('id', fixtureId);

    if (error) {
      throw error;
    }

    // If the fixture was assigned to a circuit, check if that circuit is now empty
    if (circuitId) {
      const { deleted, error: deleteError } = await deleteEmptyCircuit(circuitId);

      // If circuit was deleted and we have a callback, call it
      if (deleted && callbacks?.onCircuitDeleted) {
        logger.log(`Circuit ${circuitId} was deleted, executing callback to update controls`);
        await callbacks.onCircuitDeleted();
      }

      if (deleteError) {
        logger.error('Error checking/deleting empty circuit:', deleteError);
      }
    }

    return { error: null };
  } catch (error) {
    logger.error('Error deleting fixture:', error);
    return { error };
  }
};

// Create a circuit group
export const createCircuitGroup = async (
  floorPlanId: string,
  name: string,
  loadType: string,
  zone: string,
  notes?: string,
  parent_circuit_id?: string,
) => {
  let retries = 0;
  const maxRetries = 3;

  while (retries < maxRetries) {
    try {
      const supabase = await getAuthenticatedSupabaseClient();
      // First get the project_id for this floor plan
      const { data: floorPlanData, error: floorPlanError } = await supabase
        .from('floor_plans')
        .select('project_id')
        .eq('id', floorPlanId)
        .single();

      if (floorPlanError) throw floorPlanError;

      if (!floorPlanData || !floorPlanData.project_id) {
        throw new Error(`Floor plan ${floorPlanId} not found or has no project_id`);
      }

      const projectId = floorPlanData.project_id;

      // For linked circuits, get the parent circuit's sequence number
      let sequenceNumber;

      if (parent_circuit_id) {
        // If linking to a parent circuit, fetch its sequence number
        const { data: parentCircuit, error: parentError } = await supabase
          .from('circuits')
          .select('sequence_number, name')
          .eq('id', parent_circuit_id)
          .single();

        if (parentError) {
          logger.error('Error fetching parent circuit:', parentError);
          throw parentError;
        }

        if (parentCircuit && parentCircuit.sequence_number) {
          sequenceNumber = parentCircuit.sequence_number;
          logger.log(`Using parent circuit's sequence number: ${sequenceNumber}`);

          // Optionally update name to include parent's name if not already specified
          if (!name.includes('(Linked)')) {
            name = `${parentCircuit.name} (Linked)`;
          }
        }
      }

      // Only determine a new sequence number if not linking to a parent
      if (!sequenceNumber) {
        // Get all floor plans in this project
        const { data: projectFloorPlans, error: projectFloorPlansError } = await supabase
          .from('floor_plans')
          .select('id')
          .eq('project_id', projectId);

        if (projectFloorPlansError) throw projectFloorPlansError;

        if (!projectFloorPlans || projectFloorPlans.length === 0) {
          throw new Error(`No floor plans found for project ${projectId}`);
        }

        // Extract floor plan IDs
        const floorPlanIds = projectFloorPlans.map(fp => fp.id);

        // Get all circuits for ALL floor plans in this project
        const { data: allCircuits, error: getAllError } = await supabase
          .from('circuits')
          .select('id, name, floor_plan_id, sequence_number, panel_id, panel_slot')
          .in('floor_plan_id', floorPlanIds)
          .order('sequence_number', { ascending: true });

        if (getAllError) throw getAllError;

        // Initialize nextSequence to 1 (default for first circuit)
        sequenceNumber = 1;

        const circuitsArray: any[] = (allCircuits || []) as any[];
        if (circuitsArray.length > 0) {
          // Extract sequence numbers, ensure they're numbers, and sort them
          const usedNumbers = circuitsArray
            .map(c =>
              c.sequence_number !== null && c.sequence_number !== undefined
                ? Number(c.sequence_number)
                : 0,
            )
            .filter(n => n > 0) // Filter out invalid numbers
            .sort((a, b) => a - b); // Sort numerically

          logger.log('Used sequence numbers across project:', usedNumbers);

          // Look for the first gap in the sequence
          let foundGap = false;

          // Start from 1 and look for the first missing number
          for (let i = 1; i <= usedNumbers.length + 1; i++) {
            if (!usedNumbers.includes(i)) {
              sequenceNumber = i;
              foundGap = true;
              logger.log(`Found a gap at sequence number ${i}`);
              break;
            }
          }

          // If no gap was found (shouldn't happen, but just in case), use the next number
          if (!foundGap) {
            sequenceNumber = usedNumbers.length > 0 ? Math.max(...usedNumbers) + 1 : 1;
            logger.log(`No gaps found, using next number: ${sequenceNumber}`);
          }
        } else {
          logger.log('No existing circuits in the project, starting with sequence number 1');
        }
      }

      logger.log(
        `Creating circuit with sequence number ${sequenceNumber} for floor plan ${floorPlanId} (attempt ${retries + 1})`,
      );

      // Create the new circuit with the determined sequence number
      const { data, error } = await supabase
        .from('circuits')
        .insert({
          floor_plan_id: floorPlanId,
          name,
          load_type: loadType,
          zone,
          notes,
          sequence_number: sequenceNumber,
          parent_circuit_id, // Include the parent_circuit_id if provided
        })
        .select()
        .single();

      if (error) {
        // If the error is a constraint violation, retry
        if (error.code === '23505' && error.details?.includes('sequence_number')) {
          logger.warn(`Sequence number ${sequenceNumber} already exists, retrying...`);
          retries++;
          continue;
        }
        throw error;
      }

      logger.log(`Successfully created circuit with sequence number ${sequenceNumber}`);
      return { data, error: null };
    } catch (error) {
      retries++;
      logger.error(`Circuit creation attempt ${retries} failed:`, error);

      if (retries >= maxRetries) {
        logger.error(`Failed to create circuit after ${maxRetries} attempts`);
        return { data: null, error };
      }

      // Wait before retrying (exponential backoff)
      await new Promise(resolve => setTimeout(resolve, 500 * Math.pow(2, retries)));
    }
  }

  return { data: null, error: new Error(`Failed to create circuit after ${maxRetries} attempts`) };
};

// Get circuit groups for a project
export const getCircuitsForProject = async (projectId: string) => {
  try {
    const supabase = await getAuthenticatedSupabaseClient();
    logger.log('Fetching circuits for project:', projectId);

    // First get all floor plans for the project
    const { data: floorPlans, error: floorPlansError } = await supabase
      .from('floor_plans')
      .select('id')
      .eq('project_id', projectId);

    if (floorPlansError) {
      throw floorPlansError;
    }

    if (!floorPlans || floorPlans.length === 0) {
      logger.log('No floor plans found for project:', projectId);
      return { data: [], error: null };
    }

    // Get all floor plan IDs
    const floorPlanIds = floorPlans.map(plan => plan.id);
    logger.log('Floor plan IDs:', floorPlanIds);

    // First, fetch all circuits for these floor plans
    const { data: circuitsData, error: circuitsError } = await supabase
      .from('circuits')
      .select(
        `
        *,
        floor_plans:floor_plan_id(name)
      `,
      )
      .in('floor_plan_id', floorPlanIds);

    if (circuitsError) {
      logger.error('Error in circuits query:', circuitsError);
      throw circuitsError;
    }

    if (!circuitsData || circuitsData.length === 0) {
      logger.log('No circuits found for these floor plans');
      return { data: [], error: null };
    }

    // Transform the data to include empty fixture arrays
    const circuits = circuitsData.map(circuit => ({
      ...circuit,
      lighting_fixtures: [],
    }));

    logger.log('Fetched circuits:', circuits.length);

    // Now fetch fixture counts for each circuit separately
    if (circuits.length > 0) {
      // For simplicity, let's just fetch all fixtures with circuit_id for these circuits
      const circuitIds = circuits.map(c => c.id);

      try {
        const { data: allFixtures, error: fixturesError } = await supabase
          .from('lighting_fixtures')
          .select('id, circuit_id, fixture_type, wattage')
          .in('circuit_id', circuitIds);

        if (fixturesError) {
          logger.error('Error fetching fixtures:', fixturesError);
        } else if (allFixtures && allFixtures.length > 0) {
          // Group fixtures by circuit_id
          for (const fixture of allFixtures) {
            const circuit = circuits.find(c => c.id === fixture.circuit_id);
            if (circuit && Array.isArray(circuit.lighting_fixtures)) {
              circuit.lighting_fixtures.push(fixture);
            }
          }

          logger.log(`Assigned ${allFixtures.length} fixtures to circuits`);
        } else {
          logger.log('No fixtures found for these circuits');
        }
      } catch (fixturesFetchError) {
        logger.error('Exception fetching fixtures:', fixturesFetchError);
        // Continue with empty fixture arrays
      }
    }

    return { data: circuits, error: null };
  } catch (error) {
    logger.error('Error fetching circuits for project:', error);
    return { data: null, error };
  }
};

// Delete a circuit and unassign its fixtures
export const deleteCircuit = async (circuitId: string) => {
  try {
    const supabase = await getAuthenticatedSupabaseClient();
    // First, unassign all fixtures from this circuit
    const { error: unassignFixturesError } = await supabase
      .from('lighting_fixtures')
      .update({ circuit_id: null })
      .eq('circuit_id', circuitId);

    if (unassignFixturesError) {
      throw unassignFixturesError;
    }

    // Unassign all controls from this circuit
    const { error: unassignControlsError } = await supabase
      .from('lighting_controls')
      .update({ circuit_id: null })
      .eq('circuit_id', circuitId);

    if (unassignControlsError) {
      throw unassignControlsError;
    }

    // Then delete the circuit
    const { error: deleteError } = await supabase.from('circuits').delete().eq('id', circuitId);

    if (deleteError) {
      throw deleteError;
    }

    return { success: true, error: null };
  } catch (error) {
    logger.error('Error deleting circuit:', error);
    return { success: false, error };
  }
};

// Update a fixture's wattage
export const updateFixtureWattage = async (fixtureId: string, wattage: number | null) => {
  try {
    const supabase = await getAuthenticatedSupabaseClient();
    const { error } = await supabase
      .from('lighting_fixtures')
      .update({ wattage })
      .eq('id', fixtureId);

    if (error) {
      throw error;
    }

    return { error: null };
  } catch (error) {
    logger.error('Error updating fixture wattage:', error);
    return { error };
  }
};

// Rename a circuit
export const renameCircuit = async (circuitId: string, newName: string) => {
  try {
    const supabase = await getAuthenticatedSupabaseClient();
    const { data, error } = await supabase
      .from('circuits')
      .update({ name: newName })
      .eq('id', circuitId)
      .select()
      .single();

    if (error) {
      throw error;
    }

    return { data, error: null };
  } catch (error) {
    logger.error('Error renaming circuit:', error);
    return { data: null, error };
  }
};

type SequenceRow = {
  id: string;
  name?: string | null;
  floor_plan_id?: string | null;
  sequence_number?: number | null;
};

const buildSequenceUpdates = (rows: SequenceRow[], floorPlanNames: Record<string, string>) => {
  let nextSequenceNumber = 1;

  return rows.reduce<
    Array<{
      id: string;
      oldSequence: number | null | undefined;
      newSequence: number;
      floorPlanId: string | null | undefined;
      floorPlanName: string;
      name?: string | null;
    }>
  >((updates, row) => {
    if (row.sequence_number !== nextSequenceNumber) {
      updates.push({
        id: row.id,
        oldSequence: row.sequence_number,
        newSequence: nextSequenceNumber,
        floorPlanId: row.floor_plan_id,
        floorPlanName: row.floor_plan_id
          ? floorPlanNames[row.floor_plan_id] || 'Unknown'
          : 'Unknown',
        name: row.name,
      });
    }

    nextSequenceNumber++;
    return updates;
  }, []);
};

// Regenerate load and centralized keypad sequence numbers project-wide.
export const regenerateProjectLoadsAndKeypads = async (floorPlanId: string) => {
  try {
    logger.log(
      `Regenerating load and keypad numbers for project containing floor plan ${floorPlanId}`,
    );

    const supabase = await getAuthenticatedSupabaseClient();

    // First, get the project ID for this floor plan
    const { data: floorPlanData, error: floorPlanError } = await supabase
      .from('floor_plans')
      .select('project_id')
      .eq('id', floorPlanId)
      .single();

    if (floorPlanError) {
      throw floorPlanError;
    }

    if (!floorPlanData || !floorPlanData.project_id) {
      throw new Error(`Floor plan ${floorPlanId} not found or has no project_id`);
    }

    const projectId = floorPlanData.project_id;

    // Get all floor plans in this project
    const { data: projectFloorPlans, error: projectFloorPlansError } = await supabase
      .from('floor_plans')
      .select('id, name')
      .eq('project_id', projectId);

    if (projectFloorPlansError) {
      throw projectFloorPlansError;
    }

    if (!projectFloorPlans || projectFloorPlans.length === 0) {
      logger.log('No floor plans found for regeneration');
      return { success: true, error: null, message: 'No floor plans to regenerate' };
    }

    // Get all floor plan IDs in this project
    const floorPlanIds = projectFloorPlans.map(fp => fp.id);

    // Get all circuits across all floor plans in the project, ordered by current sequence number
    const { data: allProjectCircuits, error: getError } = await supabase
      .from('circuits')
      .select('id, name, floor_plan_id, sequence_number, panel_id, panel_slot, created_at')
      .in('floor_plan_id', floorPlanIds)
      .order('sequence_number', { ascending: true, nullsFirst: false })
      .order('created_at', { ascending: true })
      .order('id', { ascending: true });

    if (getError) {
      throw getError;
    }

    // Get all centralized keypads across all floor plans in the project.
    const { data: allProjectKeypads, error: keypadsError } = await supabase
      .from('lighting_controls')
      .select('id, name, floor_plan_id, sequence_number, type, created_at')
      .eq('type', 'centralized-keypad')
      .in('floor_plan_id', floorPlanIds)
      .order('sequence_number', { ascending: true, nullsFirst: false })
      .order('created_at', { ascending: true })
      .order('id', { ascending: true });

    if (keypadsError) {
      throw keypadsError;
    }

    // Create a mapping of floor plan IDs to names for better logging
    const floorPlanNames: Record<string, string> = {};
    projectFloorPlans.forEach(fp => {
      floorPlanNames[fp.id] = fp.name;
    });

    const circuitUpdates = buildSequenceUpdates(
      (allProjectCircuits || []) as SequenceRow[],
      floorPlanNames,
    );
    const keypadUpdates = buildSequenceUpdates(
      (allProjectKeypads || []) as SequenceRow[],
      floorPlanNames,
    );
    const totalUpdates = circuitUpdates.length + keypadUpdates.length;

    // If no updates needed, return early
    if (totalUpdates === 0) {
      logger.log('No load or keypad sequence number updates needed');

      if (
        (!allProjectCircuits || allProjectCircuits.length === 0) &&
        (!allProjectKeypads || allProjectKeypads.length === 0)
      ) {
        return {
          success: true,
          error: null,
          projectId,
          message: 'No project loads or keypads to regenerate',
        };
      }

      return {
        success: true,
        error: null,
        projectId,
        message: 'Project loads and keypads already in sequence',
      };
    }

    logger.log(
      `Updating sequence numbers for ${circuitUpdates.length} loads and ${keypadUpdates.length} keypads across ${projectFloorPlans.length} floor plans:`,
      { circuitUpdates, keypadUpdates },
    );

    // Update each circuit with its new sequence number
    // Note: Using a transaction would be better for data consistency, but for simplicity we're using individual updates
    for (const update of circuitUpdates) {
      const { error: updateError } = await supabase
        .from('circuits')
        .update({ sequence_number: update.newSequence })
        .eq('id', update.id);

      if (updateError) {
        throw updateError;
      }

      logger.log(
        `Updated load ${update.name} from ${update.oldSequence} to ${update.newSequence} in floor plan "${update.floorPlanName}"`,
      );
    }

    // Update each centralized keypad with its new sequence number
    for (const update of keypadUpdates) {
      const { error: updateError } = await supabase
        .from('lighting_controls')
        .update({ sequence_number: update.newSequence })
        .eq('id', update.id);

      if (updateError) {
        throw updateError;
      }

      logger.log(
        `Updated centralized keypad ${update.name} from ${update.oldSequence} to ${update.newSequence} in floor plan "${update.floorPlanName}"`,
      );
    }

    const updatedParts = [
      circuitUpdates.length
        ? `${circuitUpdates.length} load${circuitUpdates.length !== 1 ? 's' : ''}`
        : null,
      keypadUpdates.length
        ? `${keypadUpdates.length} keypad${keypadUpdates.length !== 1 ? 's' : ''}`
        : null,
    ].filter(Boolean);

    return {
      success: true,
      error: null,
      projectId,
      message: `Updated ${updatedParts.join(' and ')} across ${projectFloorPlans.length} floor plan${projectFloorPlans.length !== 1 ? 's' : ''}`,
    };
  } catch (error) {
    logger.error('Error regenerating load and keypad numbers:', error);
    return { success: false, error, message: 'Failed to regenerate project loads and keypads' };
  }
};

// Backward-compatible export for older callers.
export const regenerateCircuits = regenerateProjectLoadsAndKeypads;

// Delete a floor plan with cascading delete of related records
export const deleteFloorPlanWithCascade = async (floorPlanId: string, filePath: string) => {
  try {
    logger.log(`Starting cascading delete for floor plan ${floorPlanId}`);
    const supabase = await getAuthenticatedSupabaseClient();

    // Step 1: Delete all lighting fixtures related to this floor plan
    // This will automatically handle updating empty circuits via the deleteFixture function
    const { data: fixtures, error: fixturesError } = await supabase
      .from('lighting_fixtures')
      .select('id')
      .eq('floor_plan_id', floorPlanId);

    if (fixturesError) {
      logger.error('Error fetching fixtures for deletion:', fixturesError);
      // Continue with other deletions even if this fails
    } else if (fixtures && fixtures.length > 0) {
      logger.log(`Deleting ${fixtures.length} fixtures for floor plan ${floorPlanId}`);

      // Delete each fixture one by one to trigger circuit cleanup
      for (const fixture of fixtures) {
        await deleteFixture(fixture.id);
      }
    }

    // Step 2: Delete all circuits related to this floor plan
    const { error: circuitsError } = await supabase
      .from('circuits')
      .delete()
      .eq('floor_plan_id', floorPlanId);

    if (circuitsError) {
      logger.error('Error deleting circuits:', circuitsError);
      // Continue with other deletions even if this fails
    }

    // Step 3: Delete all lighting controls related to this floor plan
    // Note: The database has ON DELETE CASCADE for lighting_controls.floor_plan_id,
    // but we'll explicitly delete them here for clarity and redundancy
    const { error: controlsError } = await supabase
      .from('lighting_controls')
      .delete()
      .eq('floor_plan_id', floorPlanId);

    if (controlsError) {
      logger.error('Error deleting lighting controls:', controlsError);
      // Continue with other deletions even if this fails
    }

    // Step 4: Delete the file from storage
    const { error: storageError } = await supabase.storage.from('floor_plans').remove([filePath]);

    if (storageError) {
      logger.error('Error deleting file from storage:', storageError);
      // Continue with database deletion even if file deletion fails
    }

    // Step 5: Delete the floor plan record itself
    const { error: dbError } = await supabase.from('floor_plans').delete().eq('id', floorPlanId);

    if (dbError) {
      throw dbError;
    }

    logger.log(`Successfully completed cascading delete for floor plan ${floorPlanId}`);
    return { success: true, error: null };
  } catch (error) {
    logger.error('Error in cascading delete:', error);
    return { success: false, error };
  }
};

// Replace a floor plan image while preserving all related data (fixtures, circuits, rooms)
export const replaceFloorPlanImage = async (
  floorPlanId: string,
  oldFilePath: string,
  newFile: File,
  userId: string,
  projectId: string,
) => {
  try {
    logger.log(`Replacing floor plan image for floor plan ${floorPlanId}`);

    const supabase = await getAuthenticatedSupabaseClient();

    // Step 1: Fetch the original floor plan metadata before replacement
    const { data: originalMetadata } = await supabase
      .from('floor_plan_metadata')
      .select('image_width, image_height, y_offset')
      .eq('floor_plan_id', floorPlanId)
      .maybeSingle();

    logger.log('Original floor plan metadata:', originalMetadata);

    // Step 2: Upload the new file to Supabase Storage
    const fileExt = getFileExtension(newFile.name);
    const fileName = `${uuidv4()}.${fileExt}`;
    const newFilePath = `${userId}/${projectId}/${fileName}`;

    logger.log(
      `Uploading new file: ${newFile.name}, size: ${newFile.size} bytes, type: ${newFile.type}`,
    );

    // Upload new file to Supabase Storage
    const { error: uploadError } = await supabase.storage
      .from('floor_plans')
      .upload(newFilePath, newFile, {
        cacheControl: '3600',
        upsert: false,
      });

    if (uploadError) {
      logger.error('Storage upload error:', uploadError);
      throw uploadError;
    }

    logger.log(`New file uploaded successfully to path: ${newFilePath}`);

    // Step 3: Update the floor_plans table with the new file path and file type
    const isSvgReplacement = newFile.type === 'image/svg+xml';

    const updatePayload: Record<string, unknown> = {
      file_path: newFilePath,
      file_type: fileExt,
      image_url: newFilePath,
      is_cad_file: isSvgReplacement,
      processing_status: 'pending', // Reset processing status
      ai_processed: false, // Reset AI processed flag
      updated_at: new Date().toISOString(), // Force update timestamp
    };

    const applyUpdate = async (payload: Record<string, unknown>) =>
      supabase.from('floor_plans').update(payload).eq('id', floorPlanId);

    let { error: updateError } = await applyUpdate(updatePayload);

    if (updateError?.message?.toLowerCase().includes('image_url')) {
      const { image_url, ...fallbackPayload } = updatePayload;
      const retry = await applyUpdate(fallbackPayload);
      updateError = retry.error ?? undefined;
    }

    if (updateError) {
      // If the update fails, clean up the uploaded file
      await supabase.storage.from('floor_plans').remove([newFilePath]);
      throw updateError;
    }

    const previousFilePathLower = oldFilePath?.toLowerCase?.() ?? '';
    const hadCadArtifacts =
      previousFilePathLower.endsWith('.svg') || previousFilePathLower.includes('/svg/');

    if (hadCadArtifacts) {
      try {
        await supabase.from('floor_plan_dwg_metadata').delete().eq('floor_plan_id', floorPlanId);
      } catch (metadataError) {
        logger.warn('Failed to clear DWG metadata during replacement', metadataError);
      }
    }

    // Step 4: Delete the old file from storage
    const { error: deleteError } = await supabase.storage.from('floor_plans').remove([oldFilePath]);

    if (deleteError) {
      logger.error('Error deleting old file from storage:', deleteError);
      // Continue even if the old file deletion fails
    }

    // Step 5: Preserve the original metadata dimensions if they exist
    const hasOriginalMetadata =
      (originalMetadata as any)?.image_width && (originalMetadata as any)?.image_height;

    if (isSvgReplacement && hasOriginalMetadata) {
      // Calculate default y_offset if it doesn't exist
      const yOffset = (originalMetadata as any).y_offset ?? 0;

      // Update the floor_plan_metadata table with original dimensions and y_offset
      await supabase.from('floor_plan_metadata').upsert(
        {
          floor_plan_id: floorPlanId,
          image_width: (originalMetadata as any).image_width,
          image_height: (originalMetadata as any).image_height,
          y_offset: yOffset, // Preserve y_offset for vertical alignment
          updated_at: new Date().toISOString(),
        },
        { onConflict: 'floor_plan_id' },
      );

      logger.log(
        `Preserved original floor plan dimensions: ${(originalMetadata as any).image_width}x${(originalMetadata as any).image_height} with y_offset: ${yOffset}`,
      );
    }
    // Only calculate new dimensions if no original metadata exists
    else if (newFile.type.startsWith('image/')) {
      try {
        const img = new Image();
        const imageUrl = URL.createObjectURL(newFile);

        const dimensions = await new Promise<{ width: number; height: number }>(
          (resolve, reject) => {
            img.onload = () => {
              resolve({
                width: img.width,
                height: img.height,
              });
            };
            img.onerror = () => reject(new Error('Failed to load image'));
            img.src = imageUrl;
          },
        );

        URL.revokeObjectURL(imageUrl);

        // Update the floor_plan_metadata table
        await supabase.from('floor_plan_metadata').upsert(
          {
            floor_plan_id: floorPlanId,
            image_width: dimensions.width,
            image_height: dimensions.height,
            y_offset: 0, // Default y_offset for new metadata
            updated_at: new Date().toISOString(),
          },
          { onConflict: 'floor_plan_id' },
        );

        logger.log(
          `Updated floor plan metadata with dimensions: ${dimensions.width}x${dimensions.height}`,
        );
      } catch (dimensionsError) {
        logger.error('Error calculating image dimensions:', dimensionsError);
        // Continue even if metadata update fails
      }
    }

    return {
      success: true,
      filePath: newFilePath,
      fileType: fileExt,
      error: null,
    };
  } catch (error) {
    logger.error('Error replacing floor plan image:', error);
    return {
      success: false,
      filePath: '',
      fileType: '',
      error,
    };
  }
};
